import { useEffect, useState } from "react";
import { AirplaneTilt, Plus, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export default function ClientAway() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients/me/away");
      setRows(data.periods || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this holiday? Sessions already cancelled will stay cancelled — re-book them manually.")) return;
    try {
      await api.delete(`/clients/me/away/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="client-away-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="tracker text-brand mb-3">[ Holiday ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Holiday & breaks.</h1>
          <p className="text-zinc-500 mt-2 max-w-xl">
            Log dates you&apos;ll be away. We&apos;ll cancel any sessions in that window automatically and unblock the calendar so nothing gets booked while you&apos;re out.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-away-btn">
          <Plus size={14} weight="bold" /> Add holiday
        </button>
      </div>

      {loading ? (
        <div className="card-flat p-6 text-zinc-500" data-testid="away-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-flat p-8 text-center text-zinc-500" data-testid="away-empty">
          <AirplaneTilt size={28} className="mx-auto mb-3 text-zinc-600" />
          <p>No holidays logged yet.</p>
          <p className="text-xs mt-1">Add one above whenever you have travel coming up.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="away-list">
          {rows.map((p) => (
            <AwayCard key={p.id} period={p} onDelete={() => remove(p.id)} />
          ))}
        </div>
      )}

      {showForm && (
        <AwayFormModal
          onClose={() => setShowForm(false)}
          onCreated={(impact) => {
            setShowForm(false);
            load();
            if (impact?.cancelled_count > 0) {
              toast.success(
                `Holiday logged. ${impact.cancelled_count} session${impact.cancelled_count === 1 ? "" : "s"} auto-cancelled.`,
              );
            } else {
              toast.success("Holiday logged");
            }
          }}
        />
      )}
    </div>
  );
}

function AwayCard({ period, onDelete }) {
  const past = new Date(period.end_date + "T23:59:59Z") < new Date();
  return (
    <div className={`card-flat p-5 flex items-start justify-between gap-4 ${past ? "opacity-60" : ""}`} data-testid={`away-card-${period.id}`}>
      <div>
        <div className="tracker text-zinc-500 text-[10px] mb-1">
          {past ? "PAST" : new Date(period.start_date + "T00:00:00Z") <= new Date() ? "AWAY NOW" : "UPCOMING"}
        </div>
        <div className="font-mono text-base">
          {fmtDate(period.start_date)} <span className="text-zinc-600">→</span> {fmtDate(period.end_date)}
        </div>
        {period.reason && <div className="text-sm text-zinc-400 mt-2">{period.reason}</div>}
      </div>
      {!past && (
        <button onClick={onDelete} className="btn-ghost !py-1.5 !px-3 text-red-400" data-testid={`del-away-${period.id}`}>
          <Trash size={12} weight="bold" /> Remove
        </button>
      )}
    </div>
  );
}

function AwayFormModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    start_date: todayPlus(1),
    end_date: todayPlus(7),
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/clients/me/away", form);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="card-flat p-8 w-full max-w-lg my-10" data-testid="away-form">
        <div className="tracker text-brand mb-2">[ Log holiday ]</div>
        <h2 className="font-display text-3xl mb-6">When are you away?</h2>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <div className="tracker text-zinc-500 text-[10px] mb-1">FROM</div>
            <input
              type="date"
              required
              value={form.start_date}
              min={todayPlus(0)}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full px-4 py-3 border bg-[#0A0A0A]"
              data-testid="away-start"
            />
          </label>
          <label className="block">
            <div className="tracker text-zinc-500 text-[10px] mb-1">TO (inclusive)</div>
            <input
              type="date"
              required
              value={form.end_date}
              min={form.start_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="w-full px-4 py-3 border bg-[#0A0A0A]"
              data-testid="away-end"
            />
          </label>
        </div>

        <label className="block mb-6">
          <div className="tracker text-zinc-500 text-[10px] mb-1">REASON (optional)</div>
          <input
            type="text"
            placeholder="Holiday, travel, illness, family…"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            className="w-full px-4 py-3 border bg-[#0A0A0A]"
            data-testid="away-reason"
          />
        </label>

        <div className="card-flat p-3 mb-6 bg-brand/5 border-brand/30 text-xs text-zinc-400 flex items-start gap-2">
          <Warning size={14} weight="bold" className="text-brand-light flex-shrink-0 mt-0.5" />
          <span>
            Any sessions already booked in this window will be auto-cancelled, refunded (or rolled over if you&apos;re on a session plan), and your coach will be notified.
          </span>
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving} data-testid="away-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="away-save">
            {saving ? "Saving…" : "Log holiday"}
          </button>
        </div>
      </form>
    </div>
  );
}
